/**
 * /app/:tenantSlug/holidays — Public-holiday calendar admin (full CRUD).
 *
 * Batch G2 — the full admin surface for managing the tenant's public-
 * holiday calendar. HR admins (company_admin / hr_manager) can add,
 * edit, and soft-delete holidays. All other authenticated users see a
 * read-only list.
 *
 * URL: /app/:tenantSlug/holidays
 *   (linked from Settings → Holidays → "Open calendar" button)
 *
 * Soft-delete semantics: DELETE /api/v1/holidays/{id} flips is_active=false.
 * Historical leave records counted against the holiday remain stable. The
 * default list shows only active holidays; "Show inactive" toggle surfaces
 * the full set.
 *
 * Migration note (2026-06-01, Next.js → Vite): replaces the old redirect
 * stub that sent users back to Settings → Holidays. The full CRUD was
 * previously only in the Next.js app (Batch G2); this is the Vite SPA port.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Calendar } from "lucide-react";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { useHolidays } from "@/hooks/use-holidays";
import { HolidayFormDialog } from "@/components/settings/holiday-form-dialog";
import { HolidayDeleteButton } from "@/components/settings/holiday-delete-button";
import {
  OptionalHolidayAllowanceNote,
  OptionalHolidayPickButton,
} from "@/components/leave/optional-holiday-pick";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/holidays")({
  component: HolidaysPage,
});

/** "1 Jan 2026" — the shared en-IN formatter (it printed "01 Jan 2026"). */
function fmtHolidayDate(iso: string): string {
  return formatDate(iso);
}

function HolidaysPage() {
  const { isHrAdmin } = useHrRoles();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const years = useMemo(
    () => [currentYear - 1, currentYear, currentYear + 1, currentYear + 2],
    [currentYear],
  );

  const q = useHolidays({ year, include_inactive: includeInactive });

  const holidays = useMemo(
    () =>
      [...(q.data?.items ?? [])].sort(
        (a, b) => a.holiday_date.localeCompare(b.holiday_date),
      ),
    [q.data],
  );
  // Wave 2 — optional (restricted) holidays get a per-employee pick column.
  const hasOptional = holidays.some((h) => h.is_optional && h.is_active);

  return (
    <div className="space-y-6 p-6">
      {/* Page header */}
      <PageHeader
        title="Holidays"
        description="Manage the public-holiday calendar for this company. Holidays are excluded from leave day-counts and timesheet non-working-day calculations."
        actions={
          isHrAdmin ? (
            // Controlled dialog — trigger lives both here and in the empty state
            <HolidayFormDialog
              open={addOpen}
              onOpenChange={setAddOpen}
              showTrigger
            />
          ) : undefined
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="holidays-year">Year</Label>
          <Select
            value={String(year)}
            onValueChange={(v) => setYear(Number(v))}
          >
            <SelectTrigger id="holidays-year" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isHrAdmin && (
          <label className="flex items-center gap-2 text-sm pointer-coarse:-mx-3.5 pointer-coarse:px-3.5 pointer-coarse:min-h-11">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input accent-primary"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />{" "}
            Show Inactive
          </label>
        )}
      </div>

      <OptionalHolidayAllowanceNote
        allowance={q.data?.optional}
        hasOptional={hasOptional}
      />

      {/* Loading */}
      {q.isLoading && (
        <div className="space-y-3">
          {["s1", "s2", "s3"].map((k) => (
            <Skeleton key={k} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Error */}
      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load holidays</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {/* Empty state */}
      {q.data && holidays.length === 0 && (
        <EmptyState
          icon={Calendar}
          title={`No Holidays for ${year}`}
          description="Add a company holiday so it shows in the calendar and is excluded from leave and attendance day-counts."
          action={
            isHrAdmin ? (
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={() => setAddOpen(true)}
              >
                Add Holiday
              </Button>
            ) : undefined
          }
        />
      )}

      {/* Phones: one card per holiday. The five-column table wrapped the
          description one word per line and pushed Status / Edit / Delete
          off-screen at 390 px. */}
      {q.data && holidays.length > 0 && (
        <ul className="divide-y rounded-md border sm:hidden" aria-label={`Holidays in ${year}`}>
          {holidays.map((h) => (
            <li key={h.id} className="space-y-1.5 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-sm text-muted-foreground">
                    {fmtHolidayDate(h.holiday_date)}
                  </p>
                  <p className="font-medium">
                    {h.name}
                    {h.is_optional && (
                      <Badge variant="outline" className="ml-2 font-normal">
                        Optional
                      </Badge>
                    )}
                  </p>
                </div>
                <div className="shrink-0">
                  {h.is_active ? (
                    <Badge
                      variant="outline"
                      className="border-green-200 bg-green-50 font-medium text-green-700"
                    >
                      Active
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="border-gray-200 bg-gray-50 font-medium text-gray-700"
                    >
                      Inactive
                    </Badge>
                  )}
                </div>
              </div>
              {h.description && (
                <p className="line-clamp-2 text-sm text-muted-foreground">{h.description}</p>
              )}
              {(hasOptional || isHrAdmin) && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {hasOptional && (
                    <OptionalHolidayPickButton holiday={h} allowance={q.data?.optional} />
                  )}
                  {isHrAdmin && (
                    <>
                      <HolidayFormDialog existing={h} />
                      {h.is_active && <HolidayDeleteButton holiday={h} />}
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Table (sm and up) */}
      {q.data && holidays.length > 0 && (
        <div className="hidden rounded-md border sm:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                {hasOptional && <TableHead>Your Day Off</TableHead>}
                {isHrAdmin && (
                  <TableHead className="text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {holidays.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="font-mono text-sm whitespace-nowrap">
                    {fmtHolidayDate(h.holiday_date)}
                  </TableCell>
                  <TableCell className="font-medium">
                    {h.name}
                    {h.is_optional && (
                      <Badge variant="outline" className="ml-2 font-normal">
                        Optional
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {h.description ?? "—"}
                  </TableCell>
                  <TableCell>
                    {h.is_active ? (
                      <Badge
                        variant="outline"
                        className="border-green-200 bg-green-50 font-medium text-green-700"
                      >
                        Active
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-gray-200 bg-gray-50 font-medium text-gray-700"
                      >
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                  {hasOptional && (
                    <TableCell>
                      <OptionalHolidayPickButton
                        holiday={h}
                        allowance={q.data?.optional}
                      />
                    </TableCell>
                  )}
                  {isHrAdmin && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <HolidayFormDialog existing={h} />
                        {h.is_active && <HolidayDeleteButton holiday={h} />}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
